/**
 * The flywheel section — one component, reused on Home and The Group (the scene
 * is never duplicated). The scene is accompanied by an ordered <ol> of the four
 * stages in the DOM, so no fact depends on WebGL.
 */
import React, { useEffect, useRef, useState } from 'react';
import SceneFrame from '../three/SceneFrame';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Eyebrow } from './ui-kit';
import { useContent } from '../i18n/LocaleProvider';

export default function FlywheelSection({ copy, id = 'flywheel' }) {
  const { FLYWHEEL } = useContent();
  const [openStage, setOpenStage] = useState(null);
  const progress = useRef(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const node = sectionRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const total = rect.height + window.innerHeight;
      progress.current = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / total));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const stage = FLYWHEEL.stages.find((s) => s.key === openStage);

  return (
    <section className="u-navy-depth u-section" id={id} ref={sectionRef} aria-labelledby={`${id}-title`}>
      <div className="u-container">
        <div
          style={{
            display: 'grid',
            gap: 48,
            gridTemplateColumns: 'minmax(0, 1fr)',
            alignItems: 'center',
          }}
          className="lg:grid-cols-2"
        >
          <div>
            <Eyebrow onNavy>{FLYWHEEL.eyebrow}</Eyebrow>
            <h2 id={`${id}-title`} style={{ marginTop: 12 }}>
              {FLYWHEEL.h2}
            </h2>
            <p className="t-body-lg t-on-navy-secondary" style={{ marginTop: 16 }}>
              {copy || FLYWHEEL.groupCopy}
            </p>

            <ol
              style={{ marginTop: 32, paddingLeft: 0, listStyle: 'none', counterReset: 'stage' }}
              data-testid="flywheel-stages"
            >
              {FLYWHEEL.stages.map((item, i) => (
                <li
                  key={item.key}
                  style={{
                    display: 'flex',
                    gap: 16,
                    padding: '14px 0',
                    borderTop: '1px solid var(--border-on-navy)',
                  }}
                >
                  <span className="t-mono" style={{ color: 'var(--cyan)', fontSize: 13, paddingTop: 2 }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <button
                      type="button"
                      onClick={() => setOpenStage(item.key)}
                      className="t-logotype"
                      style={{
                        background: 'transparent',
                        border: 0,
                        color: '#fff',
                        fontSize: 17,
                        cursor: 'pointer',
                        padding: 0,
                        textAlign: 'left',
                      }}
                      data-testid={`flywheel-stage-${item.key}`}
                    >
                      {item.title}
                    </button>
                    <p className="t-small t-on-navy-secondary" style={{ marginTop: 4 }}>
                      {item.copy}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="t-mono" style={{ marginTop: 24, color: 'var(--cyan)', fontSize: 13 }}>
              {FLYWHEEL.closing}
            </p>
          </div>

          <SceneFrame
            load={() => import('../three/scenes/Flywheel3D')}
            poster="/posters/flywheel-3d.webp"
            ariaLabel="A slowly turning three-dimensional wheel with four illuminated nodes — Distribution, AI and MarTech, Technology Jumpstart, and Capital and Talent — connected by light pulses travelling in one direction."
            className="scene-frame--rounded"
            style={{ aspectRatio: '4 / 3', width: '100%' }}
            sceneProps={{ onSelectNode: setOpenStage, scrollProgress: progress }}
          />
        </div>
      </div>

      <Dialog open={Boolean(stage)} onOpenChange={(open) => !open && setOpenStage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{stage?.title}</DialogTitle>
            <DialogDescription>{stage?.copy}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </section>
  );
}
